import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { triggerAlert } from "../../utils";
import { AdminPortRequests } from "../../utils/APIClient";
import { confirm } from "../../common/ConfirmDialog";

/**
 * Review dialog for one PortRequest.
 *
 * Shows everything the losing carrier will ask for, including the account
 * number and PIN — which the backend returns only on this detail endpoint,
 * never in the list and never to the customer. They are the credentials that
 * would let someone port the number away, so they are masked here until the
 * reviewer explicitly reveals them; a shoulder-surfer in an open-plan office
 * is the realistic threat, not a determined attacker.
 *
 * Which buttons appear is driven by `allowed_transitions` from the API rather
 * than recomputed here — the server validates the transition anyway, and
 * duplicating the state machine in the UI is how the two drift apart.
 */

const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
};

function Field({ label, value, mono }) {
    return (
        <div className="col-md-6 mb-3">
            <div className="text-muted small">{label}</div>
            <div style={mono ? { fontFamily: "monospace" } : undefined}>
                {value || <span className="text-muted">—</span>}
            </div>
        </div>
    );
}

/** Carrier credentials, hidden behind a click. */
function Secret({ label, value }) {
    const [shown, setShown] = useState(false);
    return (
        <div className="col-md-6 mb-3">
            <div className="text-muted small">{label}</div>
            {!value ? (
                <span className="text-muted">—</span>
            ) : (
                <div className="d-flex align-items-center gap-2">
                    <span style={{ fontFamily: "monospace" }}>
                        {shown ? value : "•".repeat(Math.min(String(value).length, 12))}
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-link p-0"
                        onClick={() => setShown((v) => !v)}
                    >
                        {shown ? "Hide" : "Reveal"}
                    </button>
                </div>
            )}
        </div>
    );
}

export default function PortRequestDetailDialog({ row, onClose, onDecision }) {
    const [busy, setBusy] = useState(false);
    const [rejectMode, setRejectMode] = useState(false);
    const [reason, setReason] = useState("");
    const [adminNotes, setAdminNotes] = useState("");
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const reasonRef = useRef(null);

    // The list response omits the carrier credentials and the LOA, so the
    // dialog refetches the full row.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        AdminPortRequests.get(row.id)
            .then((r) => {
                if (cancelled) return;
                setDetail(r.data?.result || null);
                setAdminNotes(r.data?.result?.admin_notes || "");
            })
            .catch(() => {
                if (!cancelled) {
                    triggerAlert("error", "Oops...", "Could not load the request.");
                }
            })
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [row.id]);

    // Same focus workaround as the phone-verification dialog: react-bootstrap
    // takes initial focus on the dialog container, so autoFocus alone loses
    // it on the same tick. Keyed on the false → true transition only.
    useEffect(() => {
        if (rejectMode && reasonRef.current) {
            const el = reasonRef.current;
            const t = setTimeout(() => el.focus(), 50);
            return () => clearTimeout(t);
        }
    }, [rejectMode]);

    const d = detail || row;
    const allowed = d.allowed_transitions || [];

    const runTransition = async (fn, successMsg) => {
        setBusy(true);
        try {
            await fn();
            triggerAlert("success", "", successMsg);
            onDecision();
        } catch (e) {
            triggerAlert(
                "error",
                "Oops...",
                e?.response?.data?.error || "That didn't work.",
            );
        } finally {
            setBusy(false);
        }
    };

    const approve = async () => {
        const ok = await confirm({
            title: "Approve this port request?",
            message:
                `Approve porting ${d.phone_numbers?.split("\n")[0] || "this number"} for ` +
                `${d.org_name}? The customer is emailed and told the request is with the carriers.`,
            confirmLabel: "Approve",
            variant: "success",
        });
        if (!ok) return;
        runTransition(
            () => AdminPortRequests.approve(d.id, adminNotes),
            "Approved. Customer notified.",
        );
    };

    const complete = async () => {
        const ok = await confirm({
            title: "Mark the port complete?",
            message:
                "Only do this once the carrier confirms the number has moved. The customer " +
                "is emailed and told to cancel their old service — doing that early is the " +
                "usual way a port fails.",
            confirmLabel: "Mark complete",
            variant: "success",
        });
        if (!ok) return;
        runTransition(
            () => AdminPortRequests.complete(d.id, adminNotes),
            "Marked complete. Customer notified.",
        );
    };

    const reject = async () => {
        const r = reason.trim();
        if (!r) {
            triggerAlert(
                "error",
                "Reason required",
                "Enter a reason so the customer knows what to fix.",
            );
            return;
        }
        runTransition(
            () => AdminPortRequests.reject(d.id, r),
            "Rejected. Reason emailed to the customer.",
        );
    };

    return (
        <Modal show onHide={busy ? undefined : onClose} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Port request — {d.org_name}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {loading ? (
                    <div className="text-muted p-3">Loading…</div>
                ) : (
                    <>
                        {d.status === "rejected" && d.rejection_reason && (
                            <div className="alert alert-warning py-2">
                                <strong>Previously rejected:</strong> {d.rejection_reason}
                            </div>
                        )}

                        <h6 className="text-muted text-uppercase small mb-2">
                            Numbers to port
                        </h6>
                        <pre
                            className="p-2 bg-light border rounded"
                            style={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}
                        >
                            {d.phone_numbers || "—"}
                        </pre>

                        <h6 className="text-muted text-uppercase small mt-3 mb-2">
                            Losing carrier account
                        </h6>
                        <div className="row g-0">
                            <Field label="Carrier" value={d.current_carrier} />
                            <Field label="Authorised name" value={d.authorized_name} />
                            <Secret label="Account number" value={d.account_number} />
                            <Secret label="Account PIN" value={d.account_pin} />
                            <div className="col-12 mb-3">
                                <div className="text-muted small">Service address</div>
                                <div style={{ whiteSpace: "pre-wrap" }}>
                                    {d.service_address || (
                                        <span className="text-muted">—</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <h6 className="text-muted text-uppercase small mb-2">
                            Letter of Authorization
                        </h6>
                        {d.loa_url ? (
                            <a
                                href={d.loa_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-secondary mb-3"
                            >
                                Open the signed LOA
                            </a>
                        ) : (
                            <div className="alert alert-warning py-2">
                                No LOA was uploaded. Carriers will not process a port without
                                one — ask the customer for it before approving.
                            </div>
                        )}

                        {d.notes && (
                            <>
                                <h6 className="text-muted text-uppercase small mb-2">
                                    Customer notes
                                </h6>
                                <p style={{ whiteSpace: "pre-wrap" }}>{d.notes}</p>
                            </>
                        )}

                        <div className="row g-0 mt-2 pt-2 border-top">
                            <Field label="Status" value={d.status} />
                            <Field label="Submitted" value={fmtDateTime(d.submitted_at)} />
                            <Field label="Reviewed" value={fmtDateTime(d.reviewed_at)} />
                            <Field label="Completed" value={fmtDateTime(d.completed_at)} />
                            <Field label="Reviewer" value={d.reviewed_by} />
                        </div>

                        <div className="mb-2">
                            <label className="form-label text-muted small">
                                Internal notes (never shown to the customer)
                            </label>
                            <textarea
                                className="form-control"
                                rows={2}
                                value={adminNotes}
                                onChange={(e) => setAdminNotes(e.target.value)}
                                placeholder="Carrier ticket number, expected FOC date…"
                            />
                        </div>

                        {rejectMode && (
                            <div className="mt-3">
                                <label className="form-label">
                                    Rejection reason{" "}
                                    <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    ref={reasonRef}
                                    className="form-control"
                                    rows={3}
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Shown to the customer verbatim — say what to fix."
                                />
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                <button
                    className="btn btn-light"
                    onClick={onClose}
                    disabled={busy}
                >
                    Close
                </button>

                {!rejectMode ? (
                    <>
                        {allowed.includes("rejected") && (
                            <button
                                className="btn btn-outline-danger"
                                onClick={() => setRejectMode(true)}
                                disabled={busy || loading}
                            >
                                Reject…
                            </button>
                        )}
                        {allowed.includes("approved") && (
                            <button
                                className="btn btn-success"
                                onClick={approve}
                                disabled={busy || loading}
                            >
                                {busy ? "Approving…" : "Approve"}
                            </button>
                        )}
                        {allowed.includes("completed") && (
                            <button
                                className="btn btn-primary"
                                onClick={complete}
                                disabled={busy || loading}
                            >
                                {busy ? "Saving…" : "Mark complete"}
                            </button>
                        )}
                        {allowed.length === 0 && !loading && (
                            <span className="text-muted small align-self-center">
                                This request is closed.
                            </span>
                        )}
                    </>
                ) : (
                    <>
                        <button
                            className="btn btn-light"
                            onClick={() => setRejectMode(false)}
                            disabled={busy}
                        >
                            Cancel rejection
                        </button>
                        <button
                            className="btn btn-danger"
                            onClick={reject}
                            disabled={busy || !reason.trim()}
                        >
                            {busy ? "Rejecting…" : "Confirm reject"}
                        </button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
}
